import { ReactElement } from "react";
import styles from "./SupportedImageInfo.module.less";
import { FormattedText } from "../FormattedText/FormattedText";
import { localize } from "client_legacy/ndaw/culture";
import { getDisplayFriendlySize } from "client_legacy/Documents/fileExtensions";
import { isNotNil } from "client/Misc/utils";
import { Space } from "../Space/Space";
import { formatList } from "client/Misc/cultureSpecificUtils";

export type SupportedImageInfoProps = {
	supportedFormats: readonly string[];
	maxFileSize: number | nil;
	dimensionRecommendation: DimensionRecommendation;
	svgMaxSize?: number;
};

export const SupportedImageInfo = (props: SupportedImageInfoProps): ReactElement => {
	const {
		supportedFormats,
		maxFileSize,
		dimensionRecommendation,
		svgMaxSize
	} = props;

	return(
		<p className={styles.SupportedImageInfo}>
			<FormattedText
				text={localize("_SupportedImageFormats")}
				thingsToReplace={[
					{ what: "{0}", filler: formatList(supportedFormats) }
				]}
			/>
			<Space />

			{svgMaxSize ? (
				<FormattedText
					text={localize("_FileSizeForPngAndSvg")}
					thingsToReplace={[
						{ what: "{0}", filler: getDisplayFriendlySize(svgMaxSize) },
						{ what: "{1}", filler: getDisplayFriendlySize(maxFileSize) }
					]}
				/>
			) : (
				<>
					{isNotNil(maxFileSize) && (
						<FormattedText
							text={localize("_MaxFileSize")}
							thingsToReplace={[
								{ what: "{0}", filler: getDisplayFriendlySize(maxFileSize) }
							]}
						/>
					)}
				</>
			)}

			<Space />

			{dimensionRecommendation.kind === DimensionRecommendationKind.Height && (
				<FormattedText
					text={localize("_RecommendedHeight")}
					thingsToReplace={[
						{ what: "{0}", filler: dimensionRecommendation.height }
					]}
				/>
			)}

			{(dimensionRecommendation.kind === DimensionRecommendationKind.Dimension) && (
				<>
					{dimensionRecommendation.shouldProposeHigherDimension ? (
						<FormattedText
							text={localize("_RecommendedSizeOrHigher")}
							thingsToReplace={[
								{ what: "{0}", filler: dimensionRecommendation.width },
								{ what: "{1}", filler: dimensionRecommendation.height }
							]}
						/>
					) : (
						<FormattedText
							text={localize("_RecommendedSize")}
							thingsToReplace={[
								{ what: "{0}", filler: dimensionRecommendation.width },
								{ what: "{1}", filler: dimensionRecommendation.height }
							]}
						/>
					)}
				</>
			)}
			{(dimensionRecommendation.kind === DimensionRecommendationKind.DimensionWithAspectRatio) && (
				<FormattedText
					text={localize("_RecommendedSizeWithAspectRatio")}
					thingsToReplace={[
						{ what: "{0}", filler: dimensionRecommendation.width },
						{ what: "{1}", filler: dimensionRecommendation.height },
						{ what: "{2}", filler: dimensionRecommendation.aspectRatio }
					]}
				/>
			)}
		</p>
	);
};
SupportedImageInfo.displayName = "SupportedImageInfo";

export enum DimensionRecommendationKind {
	Height = 1,
	Dimension = 2,
	DimensionWithAspectRatio = 3
};

export type DimensionRecommendation = {
	kind: DimensionRecommendationKind.Height;
	height: number;
} | {
	kind: DimensionRecommendationKind.Dimension;
	width: number;
	height: number;
	shouldProposeHigherDimension?: boolean;
} | {
	kind: DimensionRecommendationKind.DimensionWithAspectRatio;
	width: number;
	height: number;
	aspectRatio: string;
};